import type {
  EvaEndpoint,
  IEvaServiceCallOptions,
} from '@springtree/eva-sdk-core-service';
import type { IEvaServiceDefinition } from '@springtree/eva-services-core';

type RequestInterceptor = (request: Request) => Promise<Request>;

type ResponseInterceptor = (
  response: Response,
  request: Request,
  { retry }: { retry: () => Promise<Response> },
) => Promise<Response>;

type LoggerInterceptor = (data: {
  serviceName: string;
  request: {
    headers: Headers;
    data: unknown;
  };
  response: {
    headers: Headers;
    data: unknown;
    status: number;
  };
}) => void;

const interceptors: {
  request: RequestInterceptor[];
  response: ResponseInterceptor[];
  logger: LoggerInterceptor[];
} = {
  request: [],
  response: [],
  logger: [],
};

export const addRequestInterceptor = (interceptor: RequestInterceptor) => {
  interceptors.request.push(interceptor);
};

export const addResponseInterceptor = (interceptor: ResponseInterceptor) => {
  interceptors.response.push(interceptor);
};

export const addLoggerInterceptor = (interceptor: LoggerInterceptor) => {
  interceptors.logger.push(interceptor);
};

const callEvaService = async <D extends IEvaServiceDefinition>(
  endpoint: EvaEndpoint,
  definition: new () => D,
  payload?: D['request'],
  options?: IEvaServiceCallOptions & {
    cache?: 'public' | 'no-store';
    revalidate?: number;
    requestedOrganizationUnitID?: string;
  },
) => {
  const service = endpoint.prepareService(definition);
  service.setRequest(payload);

  let request = service.buildFetchRequest(options);

  const execute = async () => {
    for (const interceptor of interceptors.request) {
      // eslint-disable-next-line no-await-in-loop
      request = await interceptor(request);
    }

    // For public calls we hard cache the response. These calls can't
    // be using user specific data, as this data will be shared across
    // users. Therefore we remove any user specific data from the request.
    if (options?.cache === 'public') {
      request.headers.delete('eva-app-token');
      request.headers.delete('eva-app-contextid');
      request.headers.delete('authorization');
    }

    if (options?.requestedOrganizationUnitID) {
      request.headers.set(
        'Eva-Requested-OrganizationUnitID',
        options.requestedOrganizationUnitID,
      );
    }

    let response = await fetch(request, {
      headers: request.headers,
      next: {
        revalidate: options?.revalidate,
        tags: options?.revalidate ? ['eva'] : undefined,
      },
      cache: options?.cache === 'public' ? 'force-cache' : 'no-store',
    });

    for (const interceptor of interceptors.response) {
      let retryResponse: Response | undefined;
      // eslint-disable-next-line no-await-in-loop
      response = await interceptor(response, request, {
        retry: execute,
      });
      if (retryResponse) return retryResponse;
    }

    return response;
  };

  const response = await execute();

  const data = await response.json();

  for (const interceptor of interceptors.logger) {
    interceptor({
      serviceName: service.data.name,
      request: {
        headers: request.headers,
        data: payload,
      },
      response: {
        headers: response.headers,
        data,
        status: response.status,
      },
    });
  }

  return data as D['response'];
};

export default callEvaService;
